import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {ChangeEvent, useCallback, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import {DialogClose} from '../../../../components/dialogs/DialogFrame/DialogClose';
import DialogApiRequestStatus from '../../../../components/dialogs/DialogApiRequestStatus';
import AssignTaskButton from '../../../../components/my-task/AssignTaskButton';
import {ITaskItem} from '../../../../dto/Task.dto';
import {apiGet, apiPost} from '../../../../axiosConfig';
import {ITnnCampaignTypes} from '../../../../react_query/tnn_sponsors/TnnCampaignTypes';
import {FileUploadWithChipViewCmpt} from '../TnnCampaignCreationStepper';
import MusicPreviewBtn from '../../../../components/music/MusicPreviewBtn';
import TrackNameCell from '../../../../components/Button/TrackNameCell';

const TnnProducerReviewFile = ({
  open,
  taskData,
  setActiveTaskSlug,
}: {
  open: boolean;
  taskData: ITaskItem;
  setActiveTaskSlug: (value: string) => void;
}) => {
  const navigate = useNavigate();
  const taskId = taskData?.id;
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [apiState, setApiState] = useState({status: '', error: null});
  const [file, setFile] = useState<{
    file: File;
    fileName: string;
    url: string;
  } | null>(null);
  const {data, isFetching} = useQuery({
    queryKey: [`v1/task/${taskId}/campaign-details-review`],
    queryFn: () =>
      apiGet(`v1/task/${taskId}/campaign-details-review`).then(response => {
        return response.data.data as ITnnCampaignTypes;
      }),
    refetchOnWindowFocus: false,
  });

  // option to edit the form when api is failed
  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null});
  }, []);

  const handleCancel = useCallback(() => {
    setApiState({status: '', error: null});
    setActiveTaskSlug('');
  }, [setActiveTaskSlug]);

  const handleClose = useCallback(() => {
    setApiState({status: '', error: null});
    setActiveTaskSlug('');
  }, [setActiveTaskSlug]);

  const handleAction = (
    action: string,
    event?: ChangeEvent<HTMLInputElement>,
  ) => {
    if (event && action === 'upload') {
      const fileObj = event.target.files && event.target.files[0];
      const fileName = fileObj ? fileObj.name.split('.')[0] : '';
      if (fileObj && fileName === data?.message.prepopulated_file_name) {
        const url = URL.createObjectURL(fileObj);
        setFile({file: fileObj, fileName: fileObj.name, url: url});
      }
    } else if (action === 'delete') {
      setFile(null);
    }
  };

  const handleSubmit = useCallback(() => {
    setApiState({status: 'pending', error: null});
    const payload = new FormData();
    payload.append(`status`, 'approved');

    if (file?.file) {
      payload.append('file', file?.file);
    }

    apiPost(`v1/task/${taskId}/campaign-details-review`, payload)
      .then(() => {
        setApiState({status: '', error: null});
        setActiveTaskSlug('');
        navigate(0);
      })
      .catch(error => {
        setApiState({status: 'error', error});
      });
  }, [taskId, file, navigate, setActiveTaskSlug]);

  const isSubmittable = useCallback(() => {
    if (taskData.task_slug === 'reupload-sponsor-rejected-campaign-file') {
      return !file?.file ? true : false;
    }
    return false;
  }, [file, taskData]);

  return (
    <Dialog
      open={open}
      maxWidth='md'
      fullWidth
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'>
      <DialogTitle>
        {taskData.task_slug === 'reupload-sponsor-rejected-campaign-file' ? (
          <Typography>Production TNN Campaign File Reupload</Typography>
        ) : (
          <Typography>Production TNN Campaign Review</Typography>
        )}
        <AssignTaskButton
          isPreview={isPreview}
          setIsPreview={setIsPreview}
          task_id={taskId}
          buttonClassName='mr-2'
        />
        <DialogClose onClose={handleCancel} />
      </DialogTitle>

      <DialogContent>
        {data &&
          !isFetching &&
          (taskData.task_slug === 'reupload-sponsor-rejected-campaign-file' ? (
            <Box>
              {data.messages.map(message =>
                message.initial_url ? (
                  <Box>
                    <Stack direction='row' spacing={4} mb={3}>
                      <Typography className='pr-7'>
                        {message.prepopulated_file_name}
                      </Typography>
                      <MusicPreviewBtn
                        id={message.id}
                        url={message.initial_url}
                        track_name={
                          message?.media_track?.track_name ??
                          message.prepopulated_file_name
                        }
                        track_artist={
                          message?.media_track?.track_artist ??
                          message.prepopulated_file_name
                        }
                      />
                      <IconButton
                        component='a'
                        href={message.initial_url}
                        target='_blank'
                        download={message.prepopulated_file_name + '.mp3'}
                        rel='noreferrer'>
                        <DownloadIcon />
                      </IconButton>
                    </Stack>
                    <Stack spacing={2}>
                      <Typography className='font-bold'>
                        Rejected Note
                      </Typography>
                      <Typography
                        sx={{border: '1px solid grey', background: 'black'}}
                        className='w-full h-100 overflow-auto scrollbar radius-5 bg-black p-3'>
                        {message.rejected_note}
                      </Typography>
                      <TrackNameCell value={message.prepopulated_file_name} />
                      <FileUploadWithChipViewCmpt
                        label='Upload TNN Campaign'
                        file={file}
                        handleAction={handleAction}
                      />
                    </Stack>
                  </Box>
                ) : null,
              )}
            </Box>
          ) : (
            <Box>
              <Typography textAlign='center' className='font-bold text-18'>
                Uploaded File
              </Typography>
              {data.messages
                .filter(message => !message.media_track_id)
                .map(message =>
                  message.initial_url ? (
                    <Box>
                      <Stack
                        direction='row'
                        spacing={4}
                        mr={2}
                        mt={2}
                        justifyContent={'center'}>
                        <Typography className='pr-7'>
                          {message.prepopulated_file_name}
                        </Typography>
                        <MusicPreviewBtn
                          id={message.id}
                          url={message.initial_url}
                          track_name={
                            message?.media_track?.track_name ??
                            message.prepopulated_file_name
                          }
                          track_artist={
                            message?.media_track?.track_artist ??
                            message.prepopulated_file_name
                          }
                        />
                      </Stack>
                    </Box>
                  ) : null,
                )}
            </Box>
          ))}
      </DialogContent>
      <DialogActions className='flex items-center justify-center'>
        <Button variant='outlined' color='info'>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={isSubmittable()}>
          {taskData.task_slug === 'reupload-sponsor-rejected-campaign-file'
            ? 'Submit'
            : 'Approve'}
        </Button>
      </DialogActions>
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleSubmit}
          onEdit={handleEdit}
          onClose={handleClose}
        />
      )}
    </Dialog>
  );
};

export default TnnProducerReviewFile;
